import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, ShieldCheck, ChevronLeft, Sparkles, Loader2, Lock, BadgeCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PLANS } from '../mockPlans';
import { formatCOP } from '../mock';
import { Button } from '../components/ui/button';
import { api, friendlyError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

export default function Checkout() {
  const { planId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const billing = params.get('billing') || 'mes';
  const isAddon = params.get('type') === 'addon';

  const item = PLANS.find(p => p.id === planId);

  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  // Addons are disabled during beta — redirect to pricing
  if (isAddon) {
    return (
      <div className="min-h-screen flex flex-col bg-habita-cream">
        <Header />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md text-center bg-white rounded-3xl p-10 border border-gray-200">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 mx-auto flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-habita-dark mt-5">Disponible próximamente</h1>
            <p className="text-gray-600 mt-3">Los add-ons individuales estarán disponibles cuando lancemos la versión oficial de habita.co.</p>
            <Button onClick={() => navigate('/precios')} className="btn-habita mt-6 rounded-full px-8 h-12">Volver a planes</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-center">
          <div>
            <p className="text-lg text-gray-600">Plan no encontrado</p>
            <Button onClick={() => navigate('/precios')} className="btn-habita mt-4 rounded-full">Ver planes</Button>
          </div>
        </div>
      </div>
    );
  }

  if (item.id === 'gratis') {
    navigate('/publicar');
    return null;
  }

  const referencePrice = item.price;

  const activate = async () => {
    if (!user) {
      navigate('/login?next=/checkout/' + item.id);
      return;
    }
    if (processing) return;
    setProcessing(true);
    try {
      await api.subscribe({
        plan_id: item.id,
        billing,
        coupon: 'BETA',
        card_brand: '—',
        card_last4: '0000',
      });
      await refresh();
      setSuccess(true);
    } catch (err) {
      toast({
        title: 'No pudimos activar tu acceso beta',
        description: friendlyError(err, 'Intenta nuevamente en unos momentos.'),
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-habita-cream">
        <SEO title={`Acceso beta activado · ${item.name}`} />
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="checkout-success">
          <div className="w-24 h-24 rounded-full bg-habita-mint mx-auto flex items-center justify-center">
            <Check className="w-12 h-12 text-habita" strokeWidth={3} />
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark mt-6">¡Acceso beta activado!</h1>
          <p className="text-gray-600 mt-3">Ya tienes acceso completo al plan <strong>{item.name}</strong> sin costo.</p>

          <div className="mt-7 bg-white rounded-2xl p-6 text-left border border-gray-200">
            <div className="flex justify-between text-sm"><span className="text-gray-500">Plan</span><span className="font-semibold">{item.name}</span></div>
            <div className="flex justify-between text-sm mt-2"><span className="text-gray-500">Acceso</span><span className="font-semibold text-emerald-600">Beta gratuita</span></div>
            <div className="flex justify-between text-sm mt-2"><span className="text-gray-500">Cobros</span><span className="font-semibold">Sin cobros durante la beta</span></div>
          </div>

          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800 flex items-start gap-3 text-left">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Te avisaremos por email con al menos <strong>30 días de anticipación</strong> antes de activar cobros cuando termine la beta. Puedes volver al plan Gratis en cualquier momento desde tu cuenta.</span>
          </div>

          <div className="mt-7 flex gap-3 justify-center flex-wrap">
            <Button onClick={() => navigate('/mi-cuenta')} className="btn-habita rounded-full px-8 h-12" data-testid="checkout-success-account-btn">Ir a mi cuenta</Button>
            <Button onClick={() => navigate('/publicar')} variant="outline" className="rounded-full px-8 h-12">Publicar propiedad</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO title={`Activar acceso beta · ${item.name}`} />
      <Header />

      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
        <button onClick={() => navigate('/precios')} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-habita" data-testid="checkout-back-btn">
          <ChevronLeft className="w-4 h-4" /> Volver a planes
        </button>

        {/* BETA BANNER */}
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <p className="font-semibold text-emerald-900">Estás activando acceso beta gratuito</p>
            <p className="text-sm text-emerald-800 mt-1">
              Durante la beta, las funcionalidades premium están disponibles <strong>sin costo</strong>. No te pediremos tarjeta de crédito. Te avisaremos con 30 días de anticipación antes de activar cobros.
            </p>
          </div>
        </div>

        <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark mt-6">
          Activa tu acceso al plan <span className="italic text-habita">{item.name}</span>
        </h1>
        <p className="text-gray-600 mt-2">{item.description}</p>

        <div className="grid md:grid-cols-3 gap-3 mt-8">
          {[
            { icon: BadgeCheck, label: 'Insignia verificada' },
            { icon: ShieldCheck, label: 'Acceso a funciones premium' },
            { icon: Lock, label: 'Sin tarjeta de crédito' },
          ].map((b, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-habita-mint text-habita flex items-center justify-center shrink-0">
                <b.icon className="w-4 h-4" />
              </span>
              <span className="text-sm font-medium text-gray-800">{b.label}</span>
            </div>
          ))}
        </div>

        {/* Plan summary */}
        <div className="mt-8 bg-white rounded-3xl border border-gray-200 p-7">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">Plan {item.name}</p>
              <p className="font-serif text-3xl font-bold text-habita-dark mt-1">{item.name}</p>
            </div>
            <div className="text-right">
              <p className="font-serif text-4xl font-bold text-emerald-600">$0</p>
              <p className="text-xs text-gray-500">durante la beta</p>
              <p className="text-xs text-gray-400 mt-1">
                <span className="line-through">{formatCOP(referencePrice)}/mes</span>
                <span className="ml-1 italic">post-beta</span>
              </p>
            </div>
          </div>

          <ul className="mt-6 space-y-3">
            {item.features.slice(0, 6).map(f => (
              <li key={f} className="flex items-start gap-2.5 text-sm">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-habita" />
                <span className="text-gray-700">{f}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={activate}
            disabled={processing}
            data-testid="activate-beta-btn"
            className="btn-habita w-full h-13 rounded-full mt-7 text-base font-semibold disabled:opacity-60"
          >
            {processing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activando acceso...</>
              : <>Activar acceso beta gratuito <Sparkles className="w-4 h-4 ml-2" /></>}
          </Button>

          <p className="text-xs text-gray-400 mt-4 text-center">
            Al activar aceptas recibir actualizaciones sobre habita.co. Puedes cancelar en cualquier momento.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
